import type { ICellRendererComp, ICellRendererParams } from 'ag-grid-community'
import { WRAPPER_CLASS_LIST } from '../constants'
import type { JSONSerializable } from './cache'
import { createWrapper, syncCellTitle } from './dom'
import type { ValidationPredicate } from './validators'
import { hasValueChanged } from './validators'

// Cell renderer configuration types with proper generics
type SimpleRenderer<T = unknown> = (data: T) => HTMLElement | null
type ContextAwareRenderer<T = unknown> = (
  data: T,
  params: ICellRendererParams
) => HTMLElement | null

type RendererFunction<T = unknown> = SimpleRenderer<T> | ContextAwareRenderer<T>

// Renderer configuration with discriminated union for different renderer types
type RendererConfig<T = unknown> = {
  readonly validate: ValidationPredicate<T>
  readonly render: RendererFunction<T>
  readonly wrapperClasses?: readonly string[]
}

/**
 * Higher-order function that creates type-safe AG Grid cell renderer classes
 */
export const createCellRenderer = <T = unknown>(
  config: RendererConfig<T>
): (new () => ICellRendererComp) => {
  class TypeSafeCellRenderer implements ICellRendererComp {
    private eGui: HTMLDivElement | undefined
    private currentValue: JSONSerializable | undefined

    /**
     * AG Grid lifecycle method - initialize the cell renderer
     */
    init(params: ICellRendererParams): void {
      try {
        this.currentValue = params.value as JSONSerializable
        this.eGui = createWrapper(config.wrapperClasses || [])
        this.buildComponent(params)
        syncCellTitle(this.eGui, this.eGui, params.value)
      } catch (error: unknown) {
        console.warn(
          '[AG Grid Renderer] Component initialization failed:',
          error
        )
        // Component initialization failed - create empty wrapper
        this.eGui = createWrapper()
        syncCellTitle(this.eGui, this.eGui, params.value)
      }
    }

    /**
     * AG Grid lifecycle method - return the DOM element
     */
    getGui(): HTMLDivElement {
      return this.eGui || createWrapper()
    }

    /**
     * AG Grid lifecycle method - refresh when data changes
     */
    refresh(params: ICellRendererParams): boolean {
      try {
        if (this.hasValueChanged(params.value)) {
          this.currentValue = params.value as JSONSerializable
          this.rebuildComponent(params)
          if (this.eGui) {
            syncCellTitle(this.eGui, this.eGui, params.value)
          }
          return true
        }
        return false
      } catch (error: unknown) {
        console.warn('[AG Grid Renderer] Component refresh failed:', error)
        // Refresh failed - component will not update
        return false
      }
    }

    /**
     * Build the component content with type-safe validation and rendering
     */
    private buildComponent(params: ICellRendererParams): void {
      if (!this.eGui) {
        return
      }

      try {
        if (!config.validate(params.value)) {
          return
        }

        // Call renderer based on function arity (1 param = simple, 2 params = context-aware)
        const element =
          config.render.length > 1
            ? (config.render as ContextAwareRenderer<T>)(
                params.value as T,
                params
              )
            : (config.render as SimpleRenderer<T>)(params.value as T)

        if (element) {
          this.eGui.appendChild(element)
        }
      } catch (error: unknown) {
        console.warn('[AG Grid Renderer] Render failed:', error)
        // Render failed - clear component
        if (this.eGui) {
          this.eGui.textContent = ''
        }
      }
    }

    /**
     * Rebuild component when data changes with proper cleanup
     */
    private rebuildComponent(params: ICellRendererParams): void {
      if (!this.eGui) {
        return
      }

      // Clean up previous state
      this.eGui.innerHTML = ''
      this.eGui.className = ''

      try {
        this.eGui.classList.add(
          ...WRAPPER_CLASS_LIST,
          ...(config.wrapperClasses || [])
        )
        this.buildComponent(params)
      } catch (_error: unknown) {
        // Rebuild failed - apply default classes only
        this.eGui.classList.add(...WRAPPER_CLASS_LIST)
      }
    }

    /**
     * Type-safe value change detection
     */
    private hasValueChanged(newValue: unknown): boolean {
      return hasValueChanged(
        newValue as JSONSerializable,
        this.currentValue as JSONSerializable
      )
    }

    /**
     * AG Grid lifecycle method - cleanup when cell is destroyed
     */
    destroy(): void {
      this.eGui = undefined
      this.currentValue = undefined
    }
  }

  return TypeSafeCellRenderer
}

// Export types for external use
export type {
  ContextAwareRenderer,
  RendererConfig,
  RendererFunction,
  SimpleRenderer
}
